import type { ITicketError } from '@/interface/ITicket';
import type { ParlayStakeError } from '@/interface/enum';
import { BetSlipCtaAction, OpenTicketStatus, PlaceOrderStatus, PlayerType, SingleModeBalanceError, TicketVoucherOptionStatus } from '@/interface/enum';
import type { IVoucherDisplay } from '@/interface/voucher';
import { getPlaceBetErrorMessage } from '@/libraries/betSlip/betSlipErrorMessage';
import { i18n } from '@/libraries/i18n';
import { toFixedDecimalPoint } from '@/libraries/utils/text';

export const GENERAL_FAILURE_MESSAGE_KEY = 'betSlip_errorMessage_generalFailure';

export const MAX_ODDS_EXCEED_MESSAGE_KEY = 'betSlip_errorMessage_maxOddsExceed';

export function buildOpenTicketError(
    messageKey: string,
    status?: ITicketError['status'],
): ITicketError {
    return {
        messageKey,
        status,
    };
}

export enum OpenTicketFailureAction {
    Remove = 'remove',
    ShowTemporaryError = 'showTemporaryError',
    ShowPersistentError = 'showPersistentError',
}

export interface IOpenTicketFailure {
    messageKey: string;
    action: OpenTicketFailureAction;
}

const EVENT_CLOSED_FAILURE: IOpenTicketFailure = {
    messageKey: 'betSlip_errorMessage_eventClosed',
    action: OpenTicketFailureAction.Remove,
};

const ODDS_CLOSED_FAILURE: IOpenTicketFailure = {
    messageKey: 'betSlip_errorMessage_oddsClosed',
    action: OpenTicketFailureAction.Remove,
};

const GENERAL_OPEN_TICKET_FAILURE: IOpenTicketFailure = {
    messageKey: GENERAL_FAILURE_MESSAGE_KEY,
    action: OpenTicketFailureAction.ShowTemporaryError,
};

const OpenTicketFailureMap: Partial<Record<OpenTicketStatus, IOpenTicketFailure>> = {
    [OpenTicketStatus.EventClosed]: EVENT_CLOSED_FAILURE,
    [OpenTicketStatus.EventCompleted]: EVENT_CLOSED_FAILURE,
    [OpenTicketStatus.EventInternal]: EVENT_CLOSED_FAILURE,
    [OpenTicketStatus.OddsClosed]: ODDS_CLOSED_FAILURE,
    [OpenTicketStatus.OddsCompleted]: ODDS_CLOSED_FAILURE,
    [OpenTicketStatus.OddsInternal]: ODDS_CLOSED_FAILURE,
    [OpenTicketStatus.MaxOddsExceed]: {
        messageKey: MAX_ODDS_EXCEED_MESSAGE_KEY,
        action: OpenTicketFailureAction.ShowPersistentError,
    },
};

export function resolveOpenTicketFailure(
    errorStatus: OpenTicketStatus | undefined,
): IOpenTicketFailure {
    if (errorStatus === undefined) {
        return GENERAL_OPEN_TICKET_FAILURE;
    }
    return OpenTicketFailureMap[errorStatus] ?? GENERAL_OPEN_TICKET_FAILURE;
}

export interface ITicketChangeSnapshot {
    point: number;
    homeScore: number;
    awayScore: number;
}

export function resolveTicketChangeError(
    previous: ITicketChangeSnapshot,
    current: ITicketChangeSnapshot,
): ITicketError | null {
    if (previous.homeScore !== current.homeScore || previous.awayScore !== current.awayScore) {
        return { messageKey: 'bet_slip_error_message_score_changed' };
    }
    if (previous.point !== current.point) {
        return {
            messageKey: 'betSlip_errorMessage_pointChanged',
            messageData: {
                oldPoint: toFixedDecimalPoint(previous.point, 2),
                newPoint: toFixedDecimalPoint(current.point, 2),
            },
        };
    }
    return null;
}

export interface IPlacedOrderErrorInput {
    message: string;
    placeOrderStatus: PlaceOrderStatus;
    extraInfo?: any;
}

export interface IPlacedOrderErrorContext {
    playerType: PlayerType;
    brandName: string;
}

export type PlacedOrderErrorBuilder = (
    input: IPlacedOrderErrorInput,
    context: IPlacedOrderErrorContext,
) => ITicketError;

export function parseAlternateStake(extraInfo: any): number | null {
    const alternateStake = Number.parseInt(String(extraInfo ?? ''));
    return Number.isNaN(alternateStake) ? null : alternateStake;
}

// Dynamic Market Place bet responses carry no message, so the status map is the
// only source of a specific message key for them.
export function buildResponseMessageError(
    input: IPlacedOrderErrorInput,
): ITicketError {
    return {
        messageKey: input.message || getPlaceBetErrorMessage(input.placeOrderStatus),
        status: input.placeOrderStatus,
    };
}

export function buildBrandedError(
    input: IPlacedOrderErrorInput,
    context: IPlacedOrderErrorContext,
): ITicketError {
    return {
        messageKey: input.message || getPlaceBetErrorMessage(input.placeOrderStatus),
        messageData: { brandName: context.brandName },
        status: input.placeOrderStatus,
    };
}

export function buildMaxWinLoseLimitError(
    input: IPlacedOrderErrorInput,
    context: IPlacedOrderErrorContext,
): ITicketError {
    return {
        messageKey: context.playerType === PlayerType.B2C
            ? 'betSlip_errorMessage_maxWinLossLimit_ContactCS'
            : 'betSlip_errorMessage_maxWinLossLimit_ContactUpperline',
        status: input.placeOrderStatus,
    };
}

export function buildLimitExceededError(
    input: IPlacedOrderErrorInput,
): ITicketError {
    const alternateStake = parseAlternateStake(input.extraInfo);
    return alternateStake === null
        ? {
            messageKey: 'betSlip_errorMessage_limitExceedTryLater',
            status: input.placeOrderStatus,
        }
        : {
            messageKey: 'betSlip_errorMessage_limitExceed',
            messageData: { alternateStake },
            status: input.placeOrderStatus,
        };
}

export function buildOrderProcessingError(
    input: IPlacedOrderErrorInput,
): ITicketError {
    return {
        messageKey: 'betSlip_errorMessage_orderProcessing',
        status: input.placeOrderStatus,
    };
}

export function buildGeneralFailureError(
    input: IPlacedOrderErrorInput,
): ITicketError {
    return {
        messageKey: GENERAL_FAILURE_MESSAGE_KEY,
        status: input.placeOrderStatus,
    };
}

export function buildStatusFallbackError(fallbackMessageKey: string): PlacedOrderErrorBuilder {
    return input => ({
        messageKey: input.message || fallbackMessageKey,
        status: input.placeOrderStatus,
    });
}

export function buildFixedMessageError(messageKey: string): PlacedOrderErrorBuilder {
    return input => ({
        messageKey,
        status: input.placeOrderStatus,
    });
}

export enum PlacedOrderFailureAction {
    ShowError = 'showError',
    Reopen = 'reopen',
    RetryOpen = 'retryOpen',
    Cancel = 'cancel',
    AdjustStake = 'adjustStake',
    RedirectToBsi = 'redirectToBsi',
}

export interface IPlacedOrderFailurePolicy {
    action: PlacedOrderFailureAction;
    buildError: PlacedOrderErrorBuilder;
}

const PlacedOrderFailurePolicyMap: Partial<Record<PlaceOrderStatus, Partial<IPlacedOrderFailurePolicy>>> = {
    [PlaceOrderStatus.PriceChange]: {
        action: PlacedOrderFailureAction.Reopen,
        buildError: buildStatusFallbackError('betSlip_errorMessage_priceChange'),
    },
    [PlaceOrderStatus.HdpPointChange]: {
        action: PlacedOrderFailureAction.Reopen,
        buildError: buildStatusFallbackError('betSlip_errorMessage_hdpPointChange'),
    },
    [PlaceOrderStatus.ScoreChanged]: {
        action: PlacedOrderFailureAction.Reopen,
        buildError: buildStatusFallbackError('bet_slip_error_message_score_changed'),
    },
    // The Voucher expired between selection and Place bet: reopening refetches the
    // Voucher list so the dropdown drops it, while the response message is kept.
    [PlaceOrderStatus.InvalidVoucher]: { action: PlacedOrderFailureAction.RetryOpen },
    [PlaceOrderStatus.RetryOpenTicket]: {
        action: PlacedOrderFailureAction.RetryOpen,
        buildError: buildGeneralFailureError,
    },
    [PlaceOrderStatus.MemberClosed]: { action: PlacedOrderFailureAction.RedirectToBsi },
    [PlaceOrderStatus.HierarachyClosed]: { action: PlacedOrderFailureAction.RedirectToBsi },
    [PlaceOrderStatus.EventClosed]: {
        action: PlacedOrderFailureAction.Cancel,
        buildError: buildStatusFallbackError('betSlip_errorMessage_eventClosed'),
    },
    [PlaceOrderStatus.InvalidPrice]: {
        action: PlacedOrderFailureAction.Cancel,
        buildError: buildStatusFallbackError('betSlip_errorMessage_oddsClosed'),
    },
    [PlaceOrderStatus.PriceSuspend]: {
        action: PlacedOrderFailureAction.Cancel,
        buildError: buildStatusFallbackError('betSlip_errorMessage_oddsClosed'),
    },
    [PlaceOrderStatus.ThirdPartyTimeout]: {
        action: PlacedOrderFailureAction.Cancel,
        buildError: buildStatusFallbackError('betSlip_errorMessage_thirdPartyTimeout'),
    },
    [PlaceOrderStatus.LimitExceed]: {
        action: PlacedOrderFailureAction.AdjustStake,
        buildError: buildLimitExceededError,
    },
    [PlaceOrderStatus.LessEvent]: {
        buildError: buildFixedMessageError('betSlip_errorMessage_lessThanThreeBets'),
    },
    [PlaceOrderStatus.MPSubBetCountExceed]: {
        buildError: buildFixedMessageError('betSlip_errorMessage_overMpTickets'),
    },
    [PlaceOrderStatus.FailToFulfillBetCountRestrictionForSplitPoint]: {
        buildError: buildGeneralFailureError,
    },
    [PlaceOrderStatus.MaxWinLoseLimitExceed]: { buildError: buildMaxWinLoseLimitError },
    [PlaceOrderStatus.OrderProcessing]: { buildError: buildOrderProcessingError },
    [PlaceOrderStatus.MemberSuspend]: { buildError: buildBrandedError },
    [PlaceOrderStatus.HierarachySuspend]: { buildError: buildBrandedError },
    [PlaceOrderStatus.AccountProblem]: { buildError: buildBrandedError },
    // These statuses also resolve to the branded account-problem message, whose
    // text interpolates the brand name.
    [PlaceOrderStatus.PTProblem]: { buildError: buildBrandedError },
    [PlaceOrderStatus.InvalidCurrencyForVietnam]: { buildError: buildBrandedError },
};

export function resolvePlacedOrderFailurePolicy(
    placeOrderStatus: PlaceOrderStatus,
): IPlacedOrderFailurePolicy {
    const policy = PlacedOrderFailurePolicyMap[placeOrderStatus];
    return {
        action: policy?.action ?? PlacedOrderFailureAction.ShowError,
        buildError: policy?.buildError ?? buildResponseMessageError,
    };
}

export function buildVoucherTicketError(
    selectedVoucher: IVoucherDisplay | null,
): ITicketError | null {
    if (selectedVoucher?.unavailableReason === TicketVoucherOptionStatus.UnavailableOddsInvalid) {
        return buildOpenTicketError('betSlip_errorMessage_voucher_oddsChanged');
    }
    return null;
}

export function formatSingleModeTicketErrorMessage(
    error: ITicketError,
    formatMoney?: (value: number) => string,
): string {
    return i18n.t(error.messageKey, formatTicketErrorMessageData(error.messageData, formatMoney));
}

function formatTicketErrorMessageData(
    messageData: ITicketError['messageData'],
    formatMoney: (value: number) => string = value => value.toString(),
): Record<string, string | number> {
    return Object.fromEntries(
        Object.entries(messageData ?? {}).map(
            ([key, value]) => [key, typeof value === 'number' ? formatMoney(value) : value],
        ),
    );
}

export function toSingleModeFooterMessage(
    error: ITicketError,
    formatMoney?: (value: number) => string,
): ISingleModeFooterMessage {
    return {
        messageKey: error.messageKey,
        messageData: formatTicketErrorMessageData(error.messageData, formatMoney),
    };
}

export interface ISingleModeFooterMessage {
    messageKey: string;
    messageData?: Record<string, string | number>;
}

export interface IPlaceBetSummaryMessage extends ISingleModeFooterMessage {
    status: 'error' | 'warning';
}

export function buildPlaceBetSummaryMessage(
    placedCount: number,
    failedCount: number,
): IPlaceBetSummaryMessage {
    if (placedCount === 0) {
        return {
            status: 'error',
            messageKey: 'bet_slip_error_message_multiple_all_fail',
        };
    }
    return {
        status: 'warning',
        messageKey: 'bet_slip_error_message_partial_bet_fail',
        messageData: { placed: placedCount, failed: failedCount },
    };
}

export interface IResolveSingleModeFooterErrorMessageInput {
    placeBetError: ISingleModeFooterMessage | null;
    balanceError: SingleModeBalanceError | null;
    invalidStakeTicketCount: number;
    ctaAction: BetSlipCtaAction;
    unacceptedOddsChangeTicketCount: number;
    playerType: PlayerType;
}

export function resolveSingleModeFooterErrorMessage(
    input: IResolveSingleModeFooterErrorMessageInput,
): ISingleModeFooterMessage | null {
    if (input.placeBetError) {
        return input.placeBetError;
    }
    const balanceErrorMessageKey = getSingleModeBalanceErrorMessageKey(input.balanceError, input.playerType);
    if (balanceErrorMessageKey) {
        return { messageKey: balanceErrorMessageKey };
    }
    if (input.invalidStakeTicketCount > 0) {
        return {
            messageKey: 'bet_slip_error_single_stake_over_under_limit',
            messageData: { number: input.invalidStakeTicketCount },
        };
    }
    if (input.ctaAction === BetSlipCtaAction.AcceptChange && input.unacceptedOddsChangeTicketCount > 0) {
        return {
            messageKey: 'bet_slip_error_single_odds_change',
            messageData: { number: input.unacceptedOddsChangeTicketCount },
        };
    }
    return null;
}

export interface IResolveParlayFooterMessageInput {
    hasSameMatchConflict: boolean;
    hasIneligibleParlayTickets: boolean;
    balanceError: SingleModeBalanceError | null;
    stakeError: ParlayStakeError | null;
    parlayMinBet: number;
    parlayMaxBet: number;
    hasUnacceptedOddsChange: boolean;
    playerType: PlayerType;
}

export function resolveParlayFooterMessage(
    input: IResolveParlayFooterMessageInput,
): ISingleModeFooterMessage | null {
    if (input.hasSameMatchConflict) {
        return { messageKey: 'bet_slip_error_parlay_selection_same_match' };
    }
    if (input.hasIneligibleParlayTickets) {
        return null;
    }
    const balanceErrorMessageKey = getSingleModeBalanceErrorMessageKey(input.balanceError, input.playerType);
    if (balanceErrorMessageKey) {
        return { messageKey: balanceErrorMessageKey };
    }
    if (input.stakeError !== null) {
        return {
            messageKey: 'bet_slip_error_parlay_stake_over_under_limit',
            messageData: {
                min: input.parlayMinBet,
                max: input.parlayMaxBet,
            },
        };
    }
    if (input.hasUnacceptedOddsChange) {
        return { messageKey: 'bet_slip_error_parlay_odds_change' };
    }
    return null;
}

export interface IResolveParlayTicketMessageInput {
    isEligible: boolean;
    subBetErrorStatus: OpenTicketStatus | undefined;
    openTicketError: ITicketError | null;
}

export function resolveParlayTicketError(
    input: IResolveParlayTicketMessageInput,
): ITicketError | null {
    if (!input.isEligible) {
        return buildOpenTicketError('bet_slip_error_parlay_selection_not_available');
    }
    if (input.subBetErrorStatus !== undefined) {
        const { messageKey } = resolveOpenTicketFailure(input.subBetErrorStatus);
        return buildOpenTicketError(messageKey, input.subBetErrorStatus);
    }
    return input.openTicketError;
}

export function getSingleModeBalanceErrorMessageKey(
    error: SingleModeBalanceError | null,
    playerType: PlayerType,
): string {
    switch (error) {
        case null:
            return '';
        case SingleModeBalanceError.BelowMinimumStake:
            return playerType === PlayerType.B2B
                ? 'bet_slip_error_insufficient_balance_b2b'
                : 'bet_slip_error_insufficient_balance_b2c';
        case SingleModeBalanceError.SingleStakeExceedsBalance:
            return 'bet_slip_error_balance_below_stake_one_bet';
        case SingleModeBalanceError.MultipleStakesExceedBalance:
            return 'bet_slip_error_balance_below_stake_multiple_single_bet';
        default: {
            const unsupportedError: never = error;
            throw new Error(`Unsupported Single mode balance error: ${unsupportedError}`);
        }
    }
}
